"use client";

import { useRef } from "react";
import Link from "next/link";
import { ProductReviews } from "./product-reviews";
import type { Product } from "@/lib/types";
import styles from "./product-info-tabs.module.css";

const tabs = [
  { id: "description", label: "توضیحات" },
  { id: "specifications", label: "مشخصات محصول" },
  { id: "reviews", label: "دیدگاه‌ها" },
] as const;
export type ProductInfoTab = (typeof tabs)[number]["id"];

export function ProductInfoTabs({
  product,
  active,
  onChange,
  reviewSlug,
}: {
  product: Product;
  active: ProductInfoTab;
  onChange: (tab: ProductInfoTab) => void;
  reviewSlug: string;
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  return (
    <section
      className={styles.section}
      id="product-information"
      aria-label="اطلاعات و دیدگاه‌های محصول"
    >
      <div className={styles.tabs} role="tablist" aria-label="اطلاعات محصول">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={(element) => {
              buttons.current[index] = element;
            }}
            type="button"
            role="tab"
            id={`product-tab-${tab.id}`}
            aria-controls={`product-panel-${tab.id}`}
            aria-selected={active === tab.id}
            tabIndex={active === tab.id ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => {
              let next = index;
              if (event.key === "ArrowLeft") next = (index + 1) % tabs.length;
              else if (event.key === "ArrowRight")
                next = (index + tabs.length - 1) % tabs.length;
              else if (event.key === "Home") next = 0;
              else if (event.key === "End") next = tabs.length - 1;
              else return;
              event.preventDefault();
              onChange(tabs[next].id);
              buttons.current[next]?.focus();
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        className={styles.panel}
        role="tabpanel"
        id="product-panel-description"
        aria-labelledby="product-tab-description"
        tabIndex={0}
        hidden={active !== "description"}
      >
        <h2>دربارهٔ {product.name}</h2>
        <p>{product.description}</p>
        <div className="detail-accordions">
          <details>
            <summary>ارسال و زمان تحویل</summary>
            <p>
              روش و هزینهٔ ارسال را در خلاصهٔ سفارش بررسی کنید.{" "}
              <Link href="/info/shipping">جزئیات ارسال</Link>
            </p>
          </details>
          <details>
            <summary>نگهداری و شرایط بازگشت</summary>
            <p>
              با دستمال نرم تمیز کنید و از حرارت و آفتاب مستقیم دور نگه دارید.
              شرایط بازگشت در راهنمای نکسو آمده است.{" "}
              <Link href="/info/returns">اطلاعات بیشتر</Link>
            </p>
          </details>
        </div>
      </div>
      <div
        className={styles.panel}
        role="tabpanel"
        id="product-panel-specifications"
        aria-labelledby="product-tab-specifications"
        tabIndex={0}
        hidden={active !== "specifications"}
      >
        <h2>مشخصات محصول</h2>
        <dl className={styles.specifications}>
          <div>
            <dt>جنس</dt>
            <dd>فیلامنت</dd>
          </div>
          {product.specs?.filter((spec) => !/اندازه|ابعاد|سایز|طول|عرض|ارتفاع|قطر|dimension|height|width|length|diameter|size/i.test(spec.label)).map((spec) => (
            <div key={`${spec.label}-${spec.value}`}><dt>{spec.label.trim() === "جنس" ? "نوع فیلامنت" : spec.label}</dt><dd>{spec.value}</dd></div>
          ))}
          <div>
            <dt>زمان آماده‌سازی</dt>
            <dd>۲ تا ۴ روز کاری</dd>
          </div>
          <div>
            <dt>روش ساخت</dt>
            <dd>چاپ سه‌بعدی، لایه‌به‌لایه</dd>
          </div>
        </dl>
      </div>
      <div
        className={styles.panel}
        role="tabpanel"
        id="product-panel-reviews"
        aria-labelledby="product-tab-reviews"
        tabIndex={0}
        hidden={active !== "reviews"}
      >
        <h2>امتیازها و دیدگاه‌ها</h2>
        <ProductReviews key={reviewSlug} slug={reviewSlug} active={active === "reviews"} />
      </div>
    </section>
  );
}
